import io from 'socket.io-client';
import { getValidToken } from '../helpers/authUtils';

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL;

/** Lazy singleton: created only when first needed (after login, when Navbar or Chat use it). */
let socketInstance = null;

/** Options so the socket reconnects reliably after idle or network drops (tab stays visible). */
const SOCKET_OPTIONS = {
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
    timeout: 20000,
};

/**
 * Returns the socket, creating it on first call. Call this only from code that runs after login
 * (e.g. Navbar, GroupChat). This way the socket connects only after login, not on app load.
 * @returns {import('socket.io-client').Socket}
 */
export function getSocket() {
    const token = getValidToken();
    if (!token) {
        disconnectAndReset();
        return null;
    }

    if (!socketInstance) {
        socketInstance = io(SOCKET_URL, {
            ...SOCKET_OPTIONS,
            auth: { sessionToken: token },
        });
    }
    return socketInstance;
}

/**
 * Returns the socket if it has already been created, otherwise null. Use this when you must not
 * create the socket (e.g. visibility disconnect/connect in App).
 * @returns {import('socket.io-client').Socket | null}
 */
export function getSocketIfExists() {
    return socketInstance ?? null;
}

/**
 * Disconnects the socket and clears the singleton. Call this on logout so the next login gets
 * a fresh connection and no background polling continues for the logged-out user.
 */
export function disconnectAndReset() {
    if (socketInstance) {
        try {
            socketInstance.disconnect();
        } catch (e) {
            // ignore
        }
        socketInstance = null;
    }
}

/**
 * Resets the socket and creates a new connection. Use when resuming after tab was hidden to avoid
 * polling with a stale session ID (sid), which can cause the server to return 400.
 * @returns {import('socket.io-client').Socket}
 */
export function reconnectSocket() {
    disconnectAndReset();
    return getSocket();
}
